import { Controller } from "@hotwired/stimulus"

// Guards the SharedClient form against saving a publicity-without-playlisting
// client without a deliberate confirmation. Publicity clients almost always
// also want playlisting; leaving playlisting unchecked silently hides the
// client's releases from SPT (Telvin).
//
// Two behaviors, both wired from the form partial:
//
// 1. syncPlaylisting fires on the publicity checkbox change event.
//    If publicity becomes checked and playlisting is unchecked, check
//    playlisting. Re-fires on every publicity change (toggling publicity
//    off and on re-checks a deliberately unchecked playlisting box); the
//    submit-time guard is the backstop that catches any deliberate
//    pub-without-playlisting submission.
//
// 2. guard fires on form submit. If the resulting state would be
//    publicity=true, playlisting=false AND either this is a create
//    OR the client was NOT already in that state, prompt via appConfirm
//    before saving. Editing an unrelated field on a client that was
//    already pub-only does NOT re-prompt, so people don't get trained
//    to click through it.
export default class extends Controller {
  static targets = ["publicity", "playlisting"]
  static values = {
    persisted: Boolean,
    originalPublicity: Boolean,
    originalPlaylisting: Boolean
  }

  connect() {
    // Capture the submit button's original state so we can restore it after
    // a preventDefault. Some forms attach their own submit listener that
    // disables the button and changes its text on first submit (e.g. to
    // show "Creating..."); when we preventDefault to show the confirm
    // dialog, that listener still runs, leaving the button disabled and
    // any subsequent requestSubmit() a no-op.
    this._submitButton = this.element.querySelector('input[type=submit], button[type=submit]')
    if (this._submitButton) {
      this._originalSubmitText = this._submitButton.value || this._submitButton.textContent
    }
  }

  syncPlaylisting() {
    // Trunk divergence from the frozen original: the department checkboxes
    // render only for bmt_admin (edit.html.erb), but the form and its
    // submit->guard wiring render for every non-intern. Without targets the
    // guard must stand down, not throw.
    if (!this.hasPublicityTarget || !this.hasPlaylistingTarget) return
    if (this.publicityTarget.checked && !this.playlistingTarget.checked) {
      this.playlistingTarget.checked = true
      // Dispatch change so downstream listeners (e.g. staff-assignment
      // dropdowns keyed on department flags) see the update.
      this.playlistingTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  async guard(event) {
    const message = this._departmentWarning() || this._budgetGapWarning()
    if (!message) return

    event.preventDefault()
    const ok = await window.appConfirm(message, { confirmLabel: "Yes", cancelLabel: "Go Back" })
    if (ok) {
      // Native form.submit() rather than requestSubmit() so we bypass every
      // submit-event listener (Stimulus, Turbo, and any inline handlers that
      // disable the button on submit). Traditional POST-and-navigate handles
      // the actual save; page navigates on server response, so button UX
      // during submission does not matter here.
      this.element.submit()
    } else {
      // Go Back: restore the button so the form is usable again.
      this._restoreSubmitButton()
    }
  }

  // Publicity without playlisting silently hides the client's releases from
  // SPT. Returns the message, or null when there is nothing to say.
  _departmentWarning() {
    if (!this.hasPublicityTarget || !this.hasPlaylistingTarget) return null
    if (!(this.publicityTarget.checked && !this.playlistingTarget.checked)) return null
    // Update path: skip the prompt if the client was already pub-only and the
    // user is editing something unrelated (not toggling either flag).
    if (this.persistedValue && this.originalPublicityValue && !this.originalPlaylistingValue) return null
    return "Please note playlisting is usually offered alongside publicity. Are you sure you want to continue?"
  }

  // The budget-gap guardrail (owner directive 2026-09-02). A playlisting
  // client saved with no budget or billing date gets a heads up, NOT a block:
  // staff routinely add a client before the money is agreed, and blocking
  // would also stop unrelated edits to any budget-less client. The 24 hour
  // email to Ben (BudgetGapSweepJob) is what makes a warning sufficient.
  //
  // It reads the same targets client-fields uses to arm the billing date, so
  // there is one notion of "the budget card is in play" in the DOM. Fields
  // absent (a role that cannot see the card) means nothing to warn about.
  _budgetGapWarning() {
    const playlisting = this.hasPlaylistingTarget
      ? this.playlistingTarget.checked
      : document.querySelector('[data-client-fields-target="playlistField"]:not(.hidden)') !== null
    if (!playlisting) return null

    const budget = this.element.querySelector("#spt_monthly_budget")
    const billingDate = this.element.querySelector("#spt_reset_day")
    if (!budget || !billingDate) return null
    if (budget.value.trim() !== "" && billingDate.value.trim() !== "") return null

    return "Heads up! This client has no playlisting budget or billing date set. " +
           "Sibyl cannot track their spend until both are in."
  }

  _restoreSubmitButton() {
    if (!this._submitButton) return
    this._submitButton.disabled = false
    if (this._originalSubmitText) {
      if ("value" in this._submitButton) {
        this._submitButton.value = this._originalSubmitText
      } else {
        this._submitButton.textContent = this._originalSubmitText
      }
    }
  }
}
